/**
 * HoursSavedDialog — "log hours saved" form entry.
 *
 * Direct-entry twin of the agent's post-export chat question
 * (record_hours_saved tool). Opens from the /hours-saved slash command in
 * DesignView; posts to POST /usage/hours-saved which emits an
 * hours_saved_declared usage event (source=form) for the admin dashboard.
 *
 * Styling matches ConfirmDialog's modal-* classes so it renders native to
 * the app shell.
 */
import React, { useEffect, useState } from 'react';
import { recordHoursSaved, updateDesign } from '../services/api';
import CustomerNameField from './CustomerNameField';

export default function HoursSavedDialog({ open, designId, designName, designs = [], onClose, onSubmitted }) {
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // When no designId is pinned (home page), the user picks one — the
  // backend requires a real, owned design to attach the event to.
  const [pickedId, setPickedId] = useState('');

  useEffect(() => {
    if (open) {
      setHours('');
      setNote('');
      setCustomerName('');
      setError('');
      setBusy(false);
      setPickedId('');
    }
  }, [open]);

  // Prefill the customer from the selected design's row; re-prefill when
  // the user switches designs so the field always reflects the selection.
  useEffect(() => {
    if (!open) return;
    const id = designId || pickedId;
    const d = designs.find((x) => (x.designId || x.workflowId || x.id) === id);
    setCustomerName(((d && d.customerName) || '').trim());
  }, [open, designId, pickedId, designs]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const effectiveId = designId || pickedId;
  const selectedDesign = designs.find((d) => (d.designId || d.workflowId || d.id) === effectiveId);
  const parsed = parseFloat(hours);
  const valid = Number.isFinite(parsed) && parsed > 0 && parsed <= 2000 && !!effectiveId && !!customerName.trim();

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    // Backfill/correct the design row's customerName when the typed value
    // differs — surfaced separately so a failed backfill isn't misreported
    // as a failed hours-saved record.
    const trimmedCustomer = customerName.trim();
    if (trimmedCustomer && trimmedCustomer !== (selectedDesign?.customerName || '').trim()) {
      try {
        await updateDesign(effectiveId, { customerName: trimmedCustomer });
      } catch (err) {
        setError(err?.response?.data?.error || 'Could not save the customer name — please try again.');
        setBusy(false);
        return;
      }
    }
    try {
      await recordHoursSaved(effectiveId, parsed, note.trim());
      onSubmitted?.(parsed);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not record — please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="modal-card hours-saved-dialog" role="dialog" aria-modal="true" aria-label="Log hours saved">
        <div className="modal-header">
          <h2>Log hours saved</h2>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: 'var(--text-secondary, rgba(0,0,0,0.65))', marginBottom: 12 }}>
            Roughly how many hours did {designName ? <strong>{designName}</strong> : 'this design'} save
            you compared to doing it manually?
          </div>
          {!designId && (
            <>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} htmlFor="hours-saved-design">
                Design
              </label>
              <select
                id="hours-saved-design"
                className="modal-input"
                value={pickedId}
                onChange={(e) => setPickedId(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
              >
                <option value="">Select a design…</option>
                {designs.map((d) => {
                  const id = d.designId || d.workflowId || d.id;
                  const label = (d.designName || '').trim() || `Untitled Design · ${String(id).slice(0, 8)}`;
                  return (
                    <option key={id} value={id}>{label}</option>
                  );
                })}
              </select>
            </>
          )}
          <div style={{ marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              Customer name
            </span>
            <CustomerNameField
              value={customerName}
              onChange={setCustomerName}
              required
              designs={designs}
              disabled={busy}
              placeholder="e.g. Luma"
              label="Customer name"
            />
          </div>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} htmlFor="hours-saved-input">
            Hours saved
          </label>
          <input
            id="hours-saved-input"
            type="number"
            min="0.5"
            max="2000"
            step="0.5"
            value={hours}
            autoFocus
            onChange={(e) => setHours(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            style={{ width: '100%', padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' }}
            placeholder="e.g. 8"
          />
          <label style={{ display: 'block', fontSize: 12, margin: '12px 0 4px' }} htmlFor="hours-saved-note">
            What did it save you? <span style={{ color: 'var(--text-tertiary, rgba(0,0,0,0.5))' }}>(optional)</span>
          </label>
          <textarea
            id="hours-saved-note"
            rows={3}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
            placeholder="e.g. journey mapping + channel specs I'd have written by hand"
          />
          {error && (
            <div style={{ color: 'var(--danger, #d32f2f)', fontSize: 12.5, marginTop: 8 }} role="alert">
              {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="modal-btn modal-btn-ghost" onClick={() => onClose?.()} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="modal-btn modal-btn-primary"
            onClick={submit}
            disabled={!valid || busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
